import { Component } from 'vue'
import { TreeNodeInput, WorkflowInput } from '@/components/Workflows/Pages/Workflow/workflow'
import { NodeTypes } from '@/components/Workflows/Pages/Editor/enums'

export default class Node {
  name: NodeTypes
  content: string = '' // The content of the + button appearing under the node
  key = '' // Unique id used for rendering
  id = '' // Unique id used for networking / saving usually same as key but may differ
  root = false // Is this node the root of the tree?
  vModel: any // Contains the output of the components vModel events
  protected _canAddNode: Boolean | Function = false // Determines if we display the + button under the node
  protected _events: Record<string, Function> = {} // Allows you to hook up to events the component emits
  component: Component // The vue component to render for the node
  protected children: Node[]
  protected parent: Node | undefined

  // Function that is called when the user clicks the + button under the node.
  // Allows the node to determine what to append to its children
  add() {}

  // Called when saving the whole tree. The basic function just calls save on its children, and
  // immediately resolves when they are done. The id contains the root id of the approver structure
  save(id: string): Promise<unknown> {
    return new Promise((resolve) => {
      Promise.all(this.children.map((c) => c.save(id))).finally(() => resolve(undefined))
    })
  }

  compile(): WorkflowInput {
    // Grab children's output
    const out: WorkflowInput[] = this.children.map((c) => c.compile())

    // Merge it
    return out.reduce(
      (acc, child) => ({
        root: acc.root.concat(child.root),
        rules: acc.rules.concat(child.rules),
        approvers: acc.approvers.concat(child.approvers),
      }),
      { root: [], rules: [], approvers: [] },
    )
  }

  get canAddNode() {
    return typeof this._canAddNode === 'function' ? this._canAddNode.bind(this)() : this._canAddNode
  }

  get events() {
    return {
      'update:modelValue': (val) => {
        this.vModel = val
      },
      ...this._events,
    }
  }

  set events(evs: Record<string, Function>) {
    const cpy = { ...evs }

    // Clean up the input and redefine the event function to take this as the first argument
    for (const key in evs) {
      if (typeof evs[key] === 'function') {
        cpy[key] = (...event) => evs[key](this, ...event)
      }
    }

    this._events = cpy
  }

  get childrenLength() {
    return this.children.length
  }

  updateParent(p: Node) {
    this.parent = p
  }

  get parentNode() {
    return this.parent
  }

  addChild(child: Node) {
    this.children.push(child)
  }

  removeNodeFromTree(node: Node) {
    if (this.children.some((vertex) => node.key === vertex.key)) {
      this.children = this.children.filter((vertex) => node.key !== vertex.key)
    } else {
      // We have not found the node, so we recurse over our children
      this.children.forEach((vertex: Node) => vertex.removeNodeFromTree(node))
    }
  }

  remove(n?: Node) {
    const node = n ?? this

    // Here we travel to the root of the tree before starting to navigate the removal of the problem node
    if (this.root) {
      this.removeNodeFromTree(node)
    } else {
      this.parent?.remove(node) // Climb up the tree
    }
  }

  constructor({
    name = NodeTypes.NODE_TRIGGER,
    content = '',
    key = '',
    root = false,
    canAddNode = false,
    component,
    children = [],
    events = undefined,
    parent = undefined,
    vModel = undefined,
  }: TreeNodeInput) {
    this.name = name
    this.content = content ?? ''
    this.key = key
    this.id = key
    this.root = root
    this._canAddNode = canAddNode ?? false
    this.events = events ?? {}
    this.component = component
    this.children = children
    this.parent = parent
    this.vModel = vModel
  }
}
